import { useState, useEffect } from 'react';
import { Upload, Plus, Edit2, Trash2, Power, PowerOff, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const API_BASE_URL = '/api';

function ManufacturerVendorPage() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        type: '',
        isActive: '',
        search: ''
    });
    const [selectedIds, setSelectedIds] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [formData, setFormData] = useState({
        type: 'MANUFACTURER',
        name: '',
        url: '',
        isActive: true,
        priority: ''
    });
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 50;

    // Sort state
    const [sortByPriority, setSortByPriority] = useState(false);

    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [isBulkDelete, setIsBulkDelete] = useState(false);

    // Calculate pagination with optional priority sort
    const sortedData = sortByPriority
        ? [...data].sort((a, b) => {
            const pA = a.priority ?? 999;
            const pB = b.priority ?? 999;
            return pA - pB;
        })
        : data;
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(sortedData.length / recordsPerPage);

    // Fetch data from API
    const fetchData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.isActive) params.append('isActive', filters.isActive);
            if (filters.search) params.append('search', filters.search);

            const response = await authFetch(`${API_BASE_URL}/manufacturers-vendors?${params}`);
            const result = await response.json();

            if (result.success) {
                setData(result.data);
            } else {
                toast.error('Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error loading data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1); // Reset to page 1 when filters change
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [filters]);

    // Handle create/update
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const url = editingRecord
                ? `${API_BASE_URL}/manufacturers-vendors/${editingRecord.id}`
                : `${API_BASE_URL}/manufacturers-vendors`;

            const method = editingRecord ? 'PUT' : 'POST';

            const response = await authFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                toast.success(result.message);
                setShowModal(false);
                resetForm();
                fetchData();
            } else {
                toast.error(result.error || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving record:', error);
            toast.error('Error saving record');
        }
    };

    // Handle delete - open modal
    const handleDelete = (id) => {
        console.log('Delete clicked for ID:', id);
        setRecordToDelete(id);
        setIsBulkDelete(false);
        setShowDeleteModal(true);
    };

    // Handle bulk delete - open modal
    const handleBulkDelete = () => {
        if (selectedIds.length === 0) {
            toast.warning('Please select records to delete');
            return;
        }
        setIsBulkDelete(true);
        setShowDeleteModal(true);
    };

    // Confirm delete
    const confirmDelete = async () => {
        setShowDeleteModal(false);

        if (isBulkDelete) {
            // Bulk Delete Logic
            try {
                const response = await authFetch(`${API_BASE_URL}/manufacturers-vendors/bulk-delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids: selectedIds })
                });

                const result = await response.json();

                if (result.success) {
                    toast.success(result.message);
                    setSelectedIds([]);
                    fetchData();
                } else {
                    toast.error(result.error || 'Bulk delete failed');
                }
            } catch (error) {
                console.error('Error bulk deleting:', error);
                toast.error('Error bulk deleting records');
            }
            setIsBulkDelete(false);
        } else {
            // Single Delete Logic
            if (!recordToDelete) return;
            const id = recordToDelete;
            setRecordToDelete(null);

            console.log('Delete confirmed, sending request to:', `${API_BASE_URL}/manufacturers-vendors/${id}`);
            try {
                const response = await authFetch(`${API_BASE_URL}/manufacturers-vendors/${id}`, {
                    method: 'DELETE'
                });

                console.log('Delete response status:', response.status);
                const result = await response.json();
                console.log('Delete result:', result);

                if (result.success) {
                    toast.success(result.message);
                    fetchData();
                } else {
                    toast.error(result.error || 'Delete failed');
                }
            } catch (error) {
                console.error('Error deleting record:', error);
                toast.error('Error deleting record');
            }
        }
    };



    // Handle toggle status
    const handleToggleStatus = async (id) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/manufacturers-vendors/${id}/toggle-status`, {
                method: 'PATCH'
            });

            const result = await response.json();

            if (result.success) {
                toast.success(result.message);
                fetchData();
            } else {
                toast.error(result.error || 'Status toggle failed');
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Error toggling status');
        }
    };

    // Handle bulk upload
    const handleBulkUpload = async (e) => {
        e.preventDefault();

        if (!uploadFile) {
            toast.error('Please select a file');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', uploadFile);

            const response = await authFetch(`${API_BASE_URL}/manufacturers-vendors/bulk-upload`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                toast.success(`Bulk import completed. Inserted: ${result.stats.inserted}, Updated: ${result.stats.updated}, Skipped: ${result.stats.skipped}`);
                setShowUploadModal(false);
                setUploadFile(null);
                fetchData();
            } else {
                toast.error(result.error || 'Upload failed');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Error uploading file');
        } finally {
            setUploading(false);
        }
    };

    // Open edit modal
    const openEditModal = (record) => {
        setEditingRecord(record);
        setFormData({
            type: record.type,
            name: record.name,
            url: record.url || '',
            isActive: record.is_active,
            priority: record.priority ?? ''
        });
        setShowModal(true);
    };

    // Reset form
    const resetForm = () => {
        setEditingRecord(null);
        setFormData({
            type: 'MANUFACTURER',
            name: '',
            url: '',
            isActive: true,
            priority: ''
        });
    };

    // Handle select all (only current page)
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(currentRecords.map(record => record.id));
        } else {
            setSelectedIds([]);
        }
    };

    // Pagination handlers
    const goToNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const goToPrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-extrabold text-slate-900">Manufacturer & Vendor URLs</h2>
                            <p className="text-sm text-slate-500 mt-1">Manage manufacturer and vendor URL database</p>
                        </div>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                                className="btn-primary"
                            >
                                <Plus className="w-4 h-4" />
                                <span>Add New</span>
                            </button>
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-semibold shadow-sm"
                            >
                                <Upload className="w-4 h-4" />
                                <span>Bulk Upload</span>
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                                value={filters.type}
                                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                                className="w-full px-3 py-2.5 text-slate-900 border border-slate-300 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-400"
                            >
                                <option value="">All Types</option>
                                <option value="MANUFACTURER">Manufacturer</option>
                                <option value="VENDOR">Vendor</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={filters.isActive}
                                onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                                className="w-full px-3 py-2.5 text-slate-900 border border-slate-300 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-400"
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                    placeholder="Search name or URL..."
                                    className="w-full pl-10 pr-3 py-2.5 text-slate-900 border border-slate-300 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-400"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bulk Actions */}
                    {selectedIds.length > 0 && (
                        <div className="mt-4 flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <span className="text-sm font-medium text-blue-900">
                                {selectedIds.length} record(s) selected
                            </span>
                            <button
                                onClick={handleBulkDelete}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                            >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete Selected</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            No records found. Add a new record or upload from Excel.
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.length === currentRecords.length && currentRecords.length > 0}
                                            onChange={handleSelectAll}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700" onClick={() => setSortByPriority(!sortByPriority)}>Priority {sortByPriority ? '▲' : '↕'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {currentRecords.map((record) => (
                                    <tr key={record.id} className="hover:bg-slate-50">
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(record.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIds([...selectedIds, record.id]);
                                                    } else {
                                                        setSelectedIds(selectedIds.filter(id => id !== record.id));
                                                    }
                                                }}
                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">{record.id}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${record.type === 'MANUFACTURER'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-purple-100 text-purple-800'
                                                }`}>
                                                {record.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.name}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {record.url ? (
                                                <a
                                                    href={record.url.startsWith('http') ? record.url : `https://${record.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 hover:text-primary-800 hover:underline"
                                                >
                                                    {record.url}
                                                </a>
                                            ) : (
                                                <span className="text-gray-400 italic">No URL</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {record.priority ? (
                                                <span className={`inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full ${
                                                    record.priority <= 30 ? 'bg-green-100 text-green-800' :
                                                    record.priority <= 70 ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>
                                                    {record.priority}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleToggleStatus(record.id)}
                                                className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${record.is_active
                                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {record.is_active ? <Power className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}
                                                <span>{record.is_active ? 'Active' : 'Inactive'}</span>
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => openEditModal(record)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(record.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination Footer */}
                {!loading && data.length > 0 && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <p className="text-sm text-gray-600">
                                Showing <span className="font-semibold">{indexOfFirstRecord + 1}</span> to <span className="font-semibold">{Math.min(indexOfLastRecord, data.length)}</span> of <span className="font-semibold">{data.length}</span> records
                            </p>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={goToPrevPage}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4 inline" /> Previous
                                </button>

                                <div className="flex space-x-1">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const page = index + 1;
                                        // Show only relevant pages (current, +/- 2, first, last)
                                        if (
                                            page === 1 ||
                                            page === totalPages ||
                                            (page >= currentPage - 2 && page <= currentPage + 2)
                                        ) {
                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => goToPage(page)}
                                                    className={`px-3 py-1 rounded-lg text-sm font-medium ${currentPage === page
                                                        ? 'bg-primary-600 text-white'
                                                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                                            return <span key={page} className="px-2 text-gray-500">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next <ChevronRight className="w-4 h-4 inline" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">
                                {editingRecord ? 'Edit Record' : 'Add New Record'}
                            </h3>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    <option value="MANUFACTURER">Manufacturer</option>
                                    <option value="VENDOR">Vendor</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    URL
                                </label>
                                <input
                                    type="text"
                                    value={formData.url}
                                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                    placeholder="https://example.com"
                                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                                    Active
                                </label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Priority <span className="text-gray-400 font-normal">(1-100)</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value ? parseInt(e.target.value) : '' })}
                                    placeholder="e.g. 1 = highest priority"
                                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                                >
                                    {editingRecord ? 'Update' : 'Create'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">Bulk Upload from Excel</h3>
                            <p className="text-sm text-gray-500 mt-1">Upload .xls or .xlsx file</p>
                        </div>
                        <form onSubmit={handleBulkUpload} className="p-6 space-y-4">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    <span className="text-primary-600 hover:text-primary-700 font-medium">
                                        Choose a file
                                    </span>
                                    <input
                                        id="file-upload"
                                        type="file"
                                        accept=".xls,.xlsx"
                                        onChange={(e) => setUploadFile(e.target.files[0])}
                                        className="hidden"
                                    />
                                </label>
                                <p className="text-xs text-gray-500 mt-1">Excel files (.xls, .xlsx)</p>
                                {uploadFile && (
                                    <p className="mt-3 text-sm text-gray-700 font-medium">
                                        Selected: {uploadFile.name}
                                    </p>
                                )}
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-xs text-blue-800">
                                    <strong>Excel Format:</strong><br />
                                    Column 1: PROPERTY (MANUFACTURER NAME or VENDOR NAME)<br />
                                    Column 2: Name<br />
                                    Column 3: URL
                                </p>
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    type="submit"
                                    disabled={!uploadFile || uploading}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowUploadModal(false);
                                        setUploadFile(null);
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-gray-700">
                                {isBulkDelete
                                    ? `Are you sure you want to delete ${selectedIds.length} selected record(s)? This action cannot be undone.`
                                    : 'Are you sure you want to delete this record? This action cannot be undone.'
                                }
                            </p>
                            <div className="flex space-x-3 pt-4">
                                <button
                                    onClick={confirmDelete}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setRecordToDelete(null);
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManufacturerVendorPage;
